import React from 'react';
import { createRoot } from 'react-dom/client';
import PropTypes from 'prop-types';
import moment from 'moment';
import { IntlProvider } from 'react-intl-redux';
import { BrowserRouter } from 'react-router-dom';
import 'normalize.css';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Provider } from 'react-redux';

import TrackingProvider from './components/TrackingProvider';
import './theme/fonts';
import './theme/utils/dna.scss';
import './theme/utils/layout.scss';
import '../../infrastructure/dataDog/init';

import App from './App';
import ConfigProvider from './components/ConfigProvider/ConfigProvider';
import { PublicationConfigProvider } from './vNext/components/PublicationConfigProvider';
import Chats from './components/Chats';
import { applyMiddleware, combineReducers, compose, createStore } from 'redux';
import middleware from './redux/middlewares';
import * as reducers from './redux/reducers';

const REACT_QUERY_STALE_TIME = 1000 * 60 * 8; // 8 minutes

const queryClient = new QueryClient({
    queryCache: new QueryCache({
        onError: (error, { meta }) => {
            if (meta.toastError.status === 'error' && meta.toast) {
                meta.toast.show(meta.toastError);
            }
        },
    }),
    defaultOptions: {
        queries: {
            refetchInterval: false,
            refetchOnMount: true,
            refetchOnReconnect: false,
            refetchIntervalInBackground: false,
            refetchOnWindowFocus: false,
            staleTime: REACT_QUERY_STALE_TIME,
        },
    },
});

/* eslint-disable no-underscore-dangle */
const composeEnhancers =
    typeof window === 'object' && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
        ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({})
        : compose;
/* eslint-enable */

const setMomentGlobalLocale = locale => moment.locale(locale);
const enhancer = composeEnhancers(
    applyMiddleware(...middleware())
);

export const Root = ({ routes, themes, language, myPageConfig, publicationConfig, store }) => (
    <BrowserRouter>
        <Provider store={ store }>
            <IntlProvider>
                <PublicationConfigProvider config={ publicationConfig }>
                    <TrackingProvider>
                        <ConfigProvider config={ myPageConfig }>
                            <QueryClientProvider client={ queryClient }>
                                <App localThemes={ themes } routes={ routes } language={ language } />
                                <ReactQueryDevtools initialIsOpen={ false } />
                            </QueryClientProvider>
                            <Chats />
                        </ConfigProvider>
                    </TrackingProvider>
                </PublicationConfigProvider>
            </IntlProvider>
        </Provider>
    </BrowserRouter>
);

Root.propTypes = {
    routes: PropTypes.node.isRequired,
    themes: PropTypes.shape({}).isRequired,
    myPageConfig: PropTypes.shape({}).isRequired,
    publicationConfig: PropTypes.shape({}).isRequired,
    language: PropTypes.string.isRequired,
    store: PropTypes.shape({}).isRequired,
};

export const renderApp = async (config) => {
    const rootElement = document.getElementById('root');

    if (!(rootElement instanceof Element)) {
        throw new Error('invalid type');
    }

    const { initialState: { intl: localeConfig } } = config;
    setMomentGlobalLocale(localeConfig.locale);
    const root = createRoot(rootElement);
    root.render(<Root { ...config } language={ localeConfig.locale } />);
};

export default (config) => {
    const store = createStore(combineReducers(reducers), config?.initialState || {}, enhancer);

    renderApp({ ...config, store });

    if (module.hot) {
        module.hot.accept('./index', () => {
            renderApp(config);
        });
    }
};
